import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Alert,
  Snackbar,
  Card,
  CardContent,
  LinearProgress,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import PaymentIcon from "@mui/icons-material/Payment";

const PaymentStatusPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [paymentStatus, setPaymentStatus] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [checkCount, setCheckCount] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [invoiceUrl, setInvoiceUrl] = useState(null);
  const [bookingCompleted, setBookingCompleted] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);

  // Get data from location state
  const appointmentData = location.state?.appointmentData;
  const transactionId = location.state?.transactionId;
  const paymentMode = location.state?.paymentMode;

  // Helper function to get session ID from cookies
  const getSessionId = () => {
    const cookies = document.cookie.split(";");
    const sessionCookie = cookies.find((cookie) =>
      cookie.trim().startsWith("session_id=")
    );
    return sessionCookie ? sessionCookie.split("=")[1] : null;
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const fetchInvoiceUrl = async () => {
    if (!transactionId) return;

    try {
      const sessionId = getSessionId();
      const headers = {
        "Content-Type": "application/json",
        "x-api-key": "76d802e426a2cb28f3760c8c8f669983f67ed775",
      };

      if (sessionId) {
        headers["Cookie"] = `session_id=${sessionId}`;
      }

      const response = await fetch(
        `https://us.uwc.world/new-uwc/uwc_expert/appointment/invoice/${transactionId}`,
        {
          method: "GET",
          headers,
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.code === 200 && data.data && data.data.invoice_url) {
          setInvoiceUrl(data.data.invoice_url);
          window.open(data.data.invoice_url, "_blank");
          return true;
        }
      }
    } catch (error) {
      console.error("Error fetching invoice URL:", error);
    }
    return false;
  };

  const checkTransactionStatus = async () => {
    if (!transactionId) {
      setError("No transaction ID found");
      setLoading(false);
      return;
    }

    try {
      const headers = {
        "Content-Type": "application/json",
        "x-api-key": "76d802e426a2cb28f3760c8c8f669983f67ed775",
      };

      const response = await fetch(
        `https://us.uwc.world/new-uwc/uwc_expert/appointment/tranaction-status/${transactionId}`,
        {
          method: "GET",
          headers: {
            ...headers,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.code === 200 && data.data) {
        const status = data.data.status;

        // Handle different status types
        if (status === false) {
          if (paymentMode === "cash") {
            // For cash payments, when status is false, show ready to confirm
            setPaymentStatus("ready_to_confirm");
            setLoading(false);
          } else {
            // For other payment modes, treat as pending
            setPaymentStatus("pending");
          }
          return;
        }

        // Handle string status values
        if (typeof status === "string") {
          setPaymentStatus(status);

          if (status === "success" || status === "completed") {
            // Payment successful
            if (paymentMode === "cash") {
              // For cash payments, show confirm button instead of auto-confirming
              setPaymentStatus("ready_to_confirm");
              setLoading(false);
            } else {
              // For Stripe payments, just show success and redirect
              showSnackbar(
                "Payment successful! Appointment booked.",
                "success"
              );
              setBookingCompleted(true);
              setTimeout(() => {
                navigate("/expert/appointments", {
                  state: {
                    message: "Appointment booked successfully!",
                    severity: "success",
                  },
                });
              }, 3000);
            }
          } else if (status === "failed" || status === "cancelled") {
            setError("Payment failed or was cancelled");
            setLoading(false);
          } else if (status === "pending") {
            // If still pending, continue checking
          } else {
            // For cash payments, treat unknown status as ready to confirm
            if (paymentMode === "cash") {
              setPaymentStatus("ready_to_confirm");
              setLoading(false);
            } else {
              setPaymentStatus("pending");
            }
          }
        } else {
          // For cash payments, treat unknown status as ready to confirm
          if (paymentMode === "cash") {
            setPaymentStatus("ready_to_confirm");
            setLoading(false);
          } else {
            setPaymentStatus("pending");
          }
        }
      } else {
        throw new Error(data.message || "Failed to check transaction status");
      }
    } catch (error) {
      console.error("Error checking transaction status:", error);
      setError(`Failed to check payment status: ${error.message}`);
      setLoading(false);
    }
  };

  const confirmPayment = async () => {
    if (!transactionId) {
      setError("Transaction ID not found");
      setLoading(false);
      return;
    }

    // Prevent multiple calls
    if (paymentConfirmed || loading) {
      return;
    }

    // Set loading state when starting confirmation
    setLoading(true);
    setError(""); // Clear any previous errors
    setPaymentConfirmed(true); // Mark as confirmed to prevent multiple clicks

    // Start timing for minimum 10-second loader
    const startTime = Date.now();
    const minLoadingTime = 10000; // 10 seconds

    try {
      const sessionId = getSessionId();
      const headers = {
        "Content-Type": "application/json",
        "x-api-key": "76d802e426a2cb28f3760c8c8f669983f67ed775",
      };

      if (sessionId) {
        headers["Cookie"] = `session_id=${sessionId}`;
      }

      const requestBody = {
        transaction_id: transactionId,
      };

      const response = await fetch(
        "https://us.uwc.world/new-uwc/uwc-expert/admin/appointment/confirm-transaction",
        {
          method: "POST",
          headers,
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();

      if (data.code === 200 && data.data) {
        showSnackbar(
          "Appointment Booked Successfully! Payment completed.",
          "success"
        );
        setBookingCompleted(true);

        // Store invoice URL if available
        if (data.data.invoice_url) {
          setInvoiceUrl(data.data.invoice_url);

          // Try to open invoice URL in new tab with error handling
          setTimeout(() => {
            try {
              const newWindow = window.open(data.data.invoice_url, "_blank");
              if (!newWindow) {
                showSnackbar(
                  'Invoice window was blocked. Please click "Open Invoice" button.',
                  "warning"
                );
              }
            } catch (error) {
              showSnackbar(
                'Could not open invoice automatically. Please click "Open Invoice" button.',
                "warning"
              );
            }
          }, 500);
        } else {
          // Try to get invoice URL from other possible fields
          const possibleInvoiceFields = [
            "invoice",
            "receipt_url",
            "payment_receipt",
            "receipt",
          ];
          for (const field of possibleInvoiceFields) {
            if (data.data[field]) {
              setInvoiceUrl(data.data[field]);
              try {
                const newWindow = window.open(data.data[field], "_blank");
                if (!newWindow) {
                  showSnackbar(
                    'Invoice window was blocked. Please click "Open Invoice" button.',
                    "warning"
                  );
                }
              } catch (error) {
                showSnackbar(
                  'Could not open invoice automatically. Please click "Open Invoice" button.',
                  "warning"
                );
              }
              break;
            }
          }
        }

        // Try to fetch invoice URL after a short delay if not already available
        if (!invoiceUrl) {
          setTimeout(() => {
            fetchInvoiceUrl();
          }, 1000);
        }

        // Wait a moment to show success message, then redirect
        setTimeout(() => {
          navigate("/expert/appointments", {
            state: {
              message: "Appointment booked successfully!",
              severity: "success",
            },
          });
        }, 5000);
      } else {
        throw new Error(data.message || "Failed to confirm payment");
      }
    } catch (error) {
      console.error("Error confirming payment:", error);
      setError(`Failed to confirm payment: ${error.message}`);
    } finally {
      // Ensure minimum 10-second loading duration
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
      
      if (remainingTime > 0) {
        setTimeout(() => {
          setLoading(false);
        }, remainingTime);
      } else {
        setLoading(false);
      }
    }
  };

  // Check status every 5 seconds
  useEffect(() => {
    if (!transactionId) {
      setError("No transaction ID provided");
      setLoading(false);
      return;
    }

    // For cash payments, check transaction status once and show confirm button
    if (paymentMode === "cash") {
      checkTransactionStatus();
      return;
    }

    // For Stripe payments, check status every 5 seconds
    const interval = setInterval(() => {
      setCheckCount((prev) => prev + 1);
      checkTransactionStatus();
    }, 5000);

    // Initial check
    checkTransactionStatus();

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, [transactionId, paymentMode, checkTransactionStatus]);

  // Stop checking after 5 minutes (150 checks)
  useEffect(() => {
    if (checkCount >= 150) {
      setError(
        "Payment check timeout. Please contact support if payment was completed."
      );
      setLoading(false);
    }
  }, [checkCount]);

  const getStatusIcon = () => {
    switch (paymentStatus) {
      case "completed":
      case "success":
        return <CheckCircleIcon sx={{ fontSize: 60, color: "success.main" }} />;
      case "failed":
      case "cancelled":
        return <ErrorIcon sx={{ fontSize: 60, color: "error.main" }} />;
      case "pending":
        return <PaymentIcon sx={{ fontSize: 60, color: "primary.main" }} />;
      case "ready_to_confirm":
        return <CheckCircleIcon sx={{ fontSize: 60, color: "success.main" }} />;
      default:
        return <PaymentIcon sx={{ fontSize: 60, color: "primary.main" }} />;
    }
  };

  const getStatusText = () => {
    if (paymentMode === "cash") {
      switch (paymentStatus) {
        case "completed":
        case "success":
          return "Transaction Confirmed!";
        case "failed":
          return "Transaction Failed";
        case "cancelled":
          return "Transaction Cancelled";
        case "pending":
          return "Confirming Transaction...";
        case "ready_to_confirm":
          return "Payment Ready to Confirm!";
        default:
          return "Confirming Transaction...";
      }
    } else {
      switch (paymentStatus) {
        case "completed":
        case "success":
          return "Payment Successful!";
        case "failed":
          return "Payment Failed";
        case "cancelled":
          return "Payment Cancelled";
        case "pending":
          return "Payment Pending...";
        default:
          return "Checking Payment Status...";
      }
    }
  };

  const getStatusColor = () => {
    switch (paymentStatus) {
      case "completed":
      case "success":
        return "success.main";
      case "failed":
      case "cancelled":
        return "error.main";
      case "pending":
        return "primary.main";
      case "ready_to_confirm":
        return "success.main";
      default:
        return "primary.main";
    }
  };

  if (!transactionId || !appointmentData) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
        gap={2}
      >
        <ErrorIcon sx={{ fontSize: 60, color: "error.main" }} />
        <Typography variant="h5" color="error.main">
          Invalid Payment Session
        </Typography>
        <Typography variant="body1" color="text.secondary">
          No transaction data found. Please try booking again.
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate("/expert/appointments")}
          sx={{ mt: 2 }}
        >
          Return to Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="grey.50"
    >
      <Card sx={{ maxWidth: 500, width: "90%", textAlign: "center" }}>
        <CardContent sx={{ p: 4 }}>
          {getStatusIcon()}

          <Typography
            variant="h4"
            sx={{ mt: 2, mb: 1, color: getStatusColor() }}
          >
            {getStatusText()}
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Transaction ID: {transactionId}
          </Typography>

          {loading && (paymentStatus === "pending" || !paymentStatus) && (
            <Box sx={{ mb: 3 }}>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Checking payment status... (Attempt {checkCount})
              </Typography>
            </Box>
          )}

          {/* Loading state for payment confirmation */}
          {loading && paymentStatus === "ready_to_confirm" && (
            <Box sx={{ mb: 3 }}>
              <CircularProgress size={24} sx={{ mr: 1, mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Confirming payment and booking appointment... (Please wait up to 10 seconds)
              </Typography>
            </Box>
          )}

          {/* Success state after payment confirmation */}
          {paymentConfirmed && !loading && paymentStatus === "ready_to_confirm" && (
            <Box sx={{ mb: 3 }}>
              <CheckCircleIcon sx={{ fontSize: 40, color: "success.main", mb: 1 }} />
              <Typography variant="body1" color="success.main" sx={{ fontWeight: 'bold' }}>
                Payment Confirmed Successfully!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your appointment is being booked. You will be redirected shortly.
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {paymentStatus === "completed" || paymentStatus === "success" ? (
            <Box>
              {!bookingCompleted ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 1 }} />
                  <Typography variant="body1">
                    Booking your appointment...
                  </Typography>
                </>
              ) : (
                <Box>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Appointment booked successfully!
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Your invoice should open automatically in a new tab. If it
                    doesn't, please click the button below.
                  </Typography>
                  {invoiceUrl ? (
                    <Button
                      variant="contained"
                      onClick={() => {
                        try {
                          const newWindow = window.open(invoiceUrl, "_blank");
                          if (!newWindow) {
                            showSnackbar(
                              "Invoice window was blocked. Please allow popups for this site.",
                              "warning"
                            );
                          }
                        } catch (error) {
                          showSnackbar(
                            "Could not open invoice. Please try again.",
                            "error"
                          );
                        }
                      }}
                      sx={{ mr: 2 }}
                    >
                      Open Invoice
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={fetchInvoiceUrl}
                      sx={{ mr: 2 }}
                    >
                      Fetch Invoice
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    onClick={() => navigate("/expert/appointments")}
                  >
                    Return to Dashboard
                  </Button>
                </Box>
              )}
            </Box>
          ) : paymentStatus === "ready_to_confirm" ? (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Cash payment is ready to be confirmed!
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Please click the button below to confirm the payment and book
                the appointment.
              </Typography>
              
              <Button
                variant="contained"
                onClick={confirmPayment}
                sx={{ mr: 2 }}
                disabled={loading || paymentConfirmed}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Confirming...
                  </>
                ) : paymentConfirmed ? (
                  "Payment Confirmed"
                ) : (
                  "Confirm Payment"
                )}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate("/expert/appointments")}
                disabled={loading || paymentConfirmed}
              >
                {paymentConfirmed ? "Continue" : "Cancel"}
              </Button>
            </Box>
          ) : paymentStatus === "failed" || paymentStatus === "cancelled" ? (
            <Box>
              <Button
                variant="contained"
                onClick={() => navigate("/expert/appointments")}
                sx={{ mr: 2 }}
              >
                Return to Dashboard
              </Button>
              <Button variant="outlined" onClick={() => window.history.back()}>
                Try Again
              </Button>
            </Box>
          ) : (
            <Box>
              {paymentMode === "cash" ? (
                <>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Cash payment detected!
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    Please confirm the payment once you have received the cash
                    from the client.
                  </Typography>
                  
                  {/* Loading state for cash payment confirmation */}
                  {loading && (
                    <Box sx={{ mb: 3 }}>
                      <CircularProgress size={24} sx={{ mr: 1, mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Confirming cash payment and booking appointment... (Please wait up to 10 seconds)
                      </Typography>
                    </Box>
                  )}

                  {/* Success state after cash payment confirmation */}
                  {paymentConfirmed && !loading && (
                    <Box sx={{ mb: 3 }}>
                      <CheckCircleIcon sx={{ fontSize: 40, color: "success.main", mb: 1 }} />
                      <Typography variant="body1" color="success.main" sx={{ fontWeight: 'bold' }}>
                        Cash Payment Confirmed Successfully!
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Your appointment is being booked. You will be redirected shortly.
                      </Typography>
                    </Box>
                  )}
                  
                  <Button
                    variant="contained"
                    onClick={confirmPayment}
                    sx={{ mr: 2 }}
                    disabled={loading || paymentConfirmed}
                  >
                    {loading ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Confirming...
                      </>
                    ) : paymentConfirmed ? (
                      "Payment Confirmed"
                    ) : (
                      "Confirm Cash Payment"
                    )}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => navigate("/expert/appointments")}
                    disabled={loading || paymentConfirmed}
                  >
                    {paymentConfirmed ? "Continue" : "Cancel"}
                  </Button>
                </>
              ) : (
                <>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Please complete your payment in the other tab. We'll check
                    the status every 5 seconds.
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => navigate("/expert/appointments")}
                  >
                    Cancel & Return
                  </Button>
                </>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentStatusPage;
